import { useTeams } from '@/hooks/useTeams';
import { supabase } from '@/lib/supabase';
import { callApi } from '@/utils/api';
import {
  getReportPeriodsQueryKey,
  getReportQueryKey,
} from '@/utils/query-keys';
import { useQuery } from '@tanstack/react-query';
import { KPI } from './useCampaigns';
import type { FacebookMedia } from './useFacebookMedia';

type MediaMetrics = {
  like_count: number | null;
  comments_count: number | null;
  reach?: number;
  shares?: number;
  total_interactions?: number;
  plays?: number;
  view_time?: number;
};

type MediaInsight = {
  name: string;
  value: {
    value: number;
  };
};

export type TopMediaItem = Pick<
  FacebookMedia,
  | 'id'
  | 'facebook_account_id'
  | 'media_type'
  | 'media_url'
  | 'thumbnail_url'
  | 'timestamp'
  | 'caption'
  | 'media_description'
> & {
  metrics: MediaMetrics;
  insights: MediaInsight[];
};

export type Report = {
  id: string;
  team_id: string;
  kpi: KPI;
  period: string;
  summary: string;
  top_media: TopMediaItem[];
  created_at: string;
  updated_at: string;
};

export const useReportPeriods = () => {
  const { activeTeam } = useTeams();

  return useQuery<Report[]>({
    queryKey: getReportPeriodsQueryKey(),
    queryFn: async () => {
      const { data, error } = await callApi(supabase, '/report/all');
      if (error) throw error;

      return data;
    },
    enabled: !!activeTeam?.data?.tokens,
  });
};

export const useReport = (id?: string, kpi?: KPI) => {
  const { activeTeam } = useTeams();

  return useQuery<Report>({
    queryKey: getReportQueryKey(id, kpi),
    queryFn: async () => {
      if (!id) throw new Error('Report ID is required');

      const endpoint = kpi ? `/report/${id}/${kpi}` : `/report/${id}`;

      const { data, error } = await callApi(supabase, endpoint);

      if (error) throw error;
      if (!data) throw new Error('Report not found');

      return data;
    },
    enabled: !!activeTeam?.data?.tokens && !!id,
  });
};
